// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useQueryClient } from "@tanstack/react-query";
import { CircleCheck, CircleX, FileSpreadsheet, SkipForward, TriangleAlert } from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, toApiError } from "@/api/api-error";
import { api } from "@/api/client";
import { useStoresQuery } from "@/api/hooks/use-stores";
import { FileDropZone } from "@/components/shared/file-drop-zone";
import { ImportPreviewTable, type PreviewRowMeta } from "@/components/shared/import-preview-table";
import { InlineSpinner } from "@/components/shared/inline-spinner";
import { VirtualSelectPopover } from "@/components/shared/virtual-select-popover";
import { WizardPane } from "@/components/shared/wizard-pane";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  ADDRESS_IMPORT_FIELDS,
  buildStoreCreateBody,
  buildStoreEditBody,
  buildStoreOperationsSection,
  CAMERA_IMPORT_FIELDS,
  type FieldSource,
  type FieldSourceMode,
  type FieldSources,
  HOURS_IMPORT_FIELDS,
  hasOperationsTimes,
  initialFieldSources,
  isFieldConfigured,
  resolveField,
  resolveOperationsTimes,
  STORE_FIELD_LABEL_KEY,
  STORE_IMPORT_FIELDS,
  type StoreImportField,
  type StoreImportFieldKey,
  TIME_RANGE_PAIRS,
} from "@/features/stores/bulk-store-import";
import { useBackofficeContext } from "@/hooks/use-backoffice-context";
import { COUNTRY_OPTIONS } from "@/lib/countries";
import { downloadCsv } from "@/lib/csv";
import { parseSpreadsheet, type SheetData } from "@/lib/spreadsheet";
import { TIMEZONE_OPTIONS } from "@/lib/timezones";
import { cn } from "@/lib/utils";

const MAP_PREVIEW_ROWS = 10;

/**
 * Outcome of one row. `skipped` means the store already existed (the API answers the create with
 * 409) — expected when re-running a sheet, so it's reported separately from a real failure.
 */
type RowStatus = "created" | "skipped" | "failed";

interface RowResult {
  rowIndex: number;
  status: RowStatus;
  error?: string;
  /** Store created, but the follow-up address/hours patch failed (store still exists). */
  patchError?: string;
}

interface BulkCreateStoresDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Step = "upload" | "map" | "preview" | "run";

export function BulkCreateStoresDialog({ open, onOpenChange }: BulkCreateStoresDialogProps) {
  const { t } = useTranslation("back-office");
  const queryClient = useQueryClient();
  const { data: context } = useBackofficeContext();

  const [step, setStep] = useState<Step>("upload");
  const [file, setFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState("");
  const [parseError, setParseError] = useState<string | null>(null);
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [sheetName, setSheetName] = useState("");
  const [sheet, setSheet] = useState<SheetData>({ headers: [], rows: [] });
  const [sources, setSources] = useState<FieldSources>(() => initialFieldSources([]));
  const readSheetRef = useRef<((name: string) => SheetData) | null>(null);

  // Whether created stores are active (false = create inactive directly, no create-then-deactivate).
  const [createActive, setCreateActive] = useState(true);
  // Optional address section: when enabled, each created store is patched with the mapped address.
  const [addressEnabled, setAddressEnabled] = useState(false);
  // Optional store-hours section. The create endpoint defaults hours server-side and takes no
  // override, so mapped hours are applied by the same follow-up patch as the address.
  const [hoursEnabled, setHoursEnabled] = useState(false);
  // Optional "copy settings from an existing store" (passed as the create endpoint's copyFrom).
  const [copyEnabled, setCopyEnabled] = useState(false);
  const [copyFrom, setCopyFrom] = useState("");
  // Row indices selected for creation (preview step). Existing/invalid rows start unchecked.
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [runTotal, setRunTotal] = useState(0);
  const [results, setResults] = useState<RowResult[]>([]);

  const chainOptions = useMemo(
    () => (context?.chains ?? []).map((c) => ({ label: c.display, value: c.value })),
    [context?.chains],
  );

  const columnOptions = useMemo(
    () =>
      sheet.headers.map((header, index) => ({
        label: header || t("bulkImport.unnamedColumn", { index: index + 1 }),
        value: String(index),
      })),
    [sheet.headers, t],
  );

  const sheetOptions = useMemo(() => sheetNames.map((name) => ({ label: name, value: name })), [sheetNames]);

  const storeOptions = useMemo(
    () => (context?.stores ?? []).map((s) => ({ label: s.display, value: s.value })),
    [context?.stores],
  );

  // Preview columns: core fields, plus each optional section's fields when it's enabled.
  const previewFields = useMemo(
    () =>
      [
        ...STORE_IMPORT_FIELDS,
        ...(hoursEnabled ? [...HOURS_IMPORT_FIELDS, ...CAMERA_IMPORT_FIELDS] : []),
        ...(addressEnabled ? ADDRESS_IMPORT_FIELDS : []),
      ].map((field) => ({ key: field.key, label: t(STORE_FIELD_LABEL_KEY[field.key]) })),
    [addressEnabled, hoursEnabled, t],
  );

  function reset() {
    setStep("upload");
    setFile(null);
    setFileName("");
    setParseError(null);
    setSheetNames([]);
    setSheetName("");
    setSheet({ headers: [], rows: [] });
    setSources(initialFieldSources([]));
    setCreateActive(true);
    setAddressEnabled(false);
    setHoursEnabled(false);
    setCopyEnabled(false);
    setCopyFrom("");
    readSheetRef.current = null;
    setSelected(new Set());
    setRunning(false);
    setProgress(0);
    setRunTotal(0);
    setResults([]);
  }

  function handleOpenChange(next: boolean) {
    if (running) return; // don't let the dialog close mid-run
    if (!next) reset();
    onOpenChange(next);
  }

  async function parseFile() {
    if (!file) return;
    setParseError(null);
    try {
      const workbook = await parseSpreadsheet(file);
      if (workbook.sheetNames.length === 0) {
        setParseError(t("bulkImport.errors.noSheets"));
        return;
      }
      readSheetRef.current = workbook.readSheet;
      const first = workbook.sheetNames[0] ?? "";
      const data = workbook.readSheet(first);
      setFileName(file.name);
      setSheetNames(workbook.sheetNames);
      setSheetName(first);
      setSheet(data);
      setSources(initialFieldSources(data.headers));
      setStep("map");
    } catch {
      setParseError(t("bulkImport.errors.parseFailed"));
    }
  }

  function handleSheetChange(name: string) {
    const data = readSheetRef.current?.(name) ?? { headers: [], rows: [] };
    setSheetName(name);
    setSheet(data);
    setSources(initialFieldSources(data.headers));
  }

  function patchSource(key: StoreImportFieldKey, patch: Partial<FieldSource>) {
    setSources((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  }

  // Per-row validation: required values resolve non-empty, chain known, store codes unique in file.
  const validation = useMemo(() => {
    const requiredConfigured = STORE_IMPORT_FIELDS.filter((f) => f.required).every((f) =>
      isFieldConfigured(sources[f.key]),
    );
    const knownChains = new Set((context?.chains ?? []).map((c) => c.value));
    const seenCodes = new Set<string>();
    const rowErrors = sheet.rows.map((row) => {
      const errors: string[] = [];
      for (const field of STORE_IMPORT_FIELDS) {
        if (field.required && !resolveField(row, sources, field.key)) {
          errors.push(t("bulkImport.errors.missing", { field: t(STORE_FIELD_LABEL_KEY[field.key]) }));
        }
      }
      const chain = resolveField(row, sources, "chainName");
      if (chain && knownChains.size > 0 && !knownChains.has(chain)) {
        errors.push(t("stores.bulkCreate.errors.unknownChain", { chain }));
      }
      const code = resolveField(row, sources, "storeCode");
      if (code) {
        if (seenCodes.has(code)) errors.push(t("stores.bulkCreate.errors.dupCode", { code }));
        else seenCodes.add(code);
      }
      // Store/camera times: catch unparseable cells and reversed ranges here rather than letting the
      // API reject them mid-import (it enforces openTime <= closeTime and onTime <= offTime).
      if (hoursEnabled) {
        const times = resolveOperationsTimes(row, sources);
        for (const field of [...HOURS_IMPORT_FIELDS, ...CAMERA_IMPORT_FIELDS]) {
          const raw = resolveField(row, sources, field.key);
          if (raw && !times[field.key as keyof typeof times]) {
            errors.push(t("stores.bulkCreate.errors.badTime", { field: t(STORE_FIELD_LABEL_KEY[field.key]), raw }));
          }
        }
        for (const pair of TIME_RANGE_PAIRS) {
          const from = times[pair.from as keyof typeof times];
          const to = times[pair.to as keyof typeof times];
          if (from && to && from > to) errors.push(t(pair.errorKey));
        }
      }
      return errors;
    });
    const invalidCount = rowErrors.filter((e) => e.length > 0).length;
    return { requiredConfigured, rowErrors, invalidCount, validCount: rowErrors.length - invalidCount };
  }, [sheet, sources, context, hoursEnabled, t]);

  // Names of stores that already exist, to auto-skip rows that would collide. `/v1/context` lists
  // active stores only, but store names are unique across the whole estate — an inactive store still
  // blocks the create — so the full list is fetched separately and unioned in.
  const { data: allStores, isPending: allStoresPending } = useStoresQuery({
    limit: -1,
    onlyActive: false,
    enabled: open,
  });
  const existingStoreNames = useMemo(() => {
    const names = new Set((context?.stores ?? []).map((s) => s.value));
    for (const store of allStores?.stores ?? []) names.add(store.storeName);
    return names;
  }, [context?.stores, allStores]);

  // Per-row preview metadata: resolved store name, validity, and whether it already exists.
  const previewRows = useMemo<PreviewRowMeta[]>(
    () =>
      sheet.rows.map((row, index) => {
        const errors = validation.rowErrors[index] ?? [];
        const storeName = resolveField(row, sources, "storeName");
        return { index, errors, valid: errors.length === 0, exists: !!storeName && existingStoreNames.has(storeName) };
      }),
    [sheet.rows, sources, validation.rowErrors, existingStoreNames],
  );

  const getCell = useCallback(
    (rowIndex: number, field: StoreImportFieldKey) => resolveField(sheet.rows[rowIndex] ?? [], sources, field),
    [sheet.rows, sources],
  );

  const selectableIndices = useMemo(() => previewRows.filter((r) => r.valid).map((r) => r.index), [previewRows]);
  const existsCount = useMemo(() => previewRows.filter((r) => r.exists).length, [previewRows]);
  const allSelectableSelected = selectableIndices.length > 0 && selectableIndices.every((i) => selected.has(i));

  function goToPreview() {
    // Default selection: valid rows that don't already exist.
    setSelected(new Set(previewRows.filter((r) => r.valid && !r.exists).map((r) => r.index)));
    setStep("preview");
  }

  function toggleRow(index: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }

  function toggleAll() {
    setSelected(allSelectableSelected ? new Set() : new Set(selectableIndices));
  }

  async function run() {
    const indices = [...selected].sort((a, b) => a - b);
    setStep("run");
    setRunning(true);
    setProgress(0);
    setRunTotal(indices.length);
    const out: RowResult[] = [];
    for (let i = 0; i < indices.length; i++) {
      const rowIndex = indices[i] ?? 0;
      const row = sheet.rows[rowIndex] ?? [];
      const body = buildStoreCreateBody(row, sources, createActive);
      try {
        const res = await api.POST("/v1/stores", {
          params: { query: { copyFrom: copyEnabled && copyFrom ? copyFrom : undefined } },
          body,
        });
        if (res.error) throw toApiError(res.error, res.response, t("stores.bulkCreate.errors.createFailed"));
        // Store created — apply the optional address and/or hours in a single follow-up patch. A
        // patch failure is recorded separately (the store still exists) rather than failing the row.
        let patchError: string | undefined;
        const wantsTimes = hoursEnabled && hasOperationsTimes(resolveOperationsTimes(row, sources));
        if (addressEnabled || wantsTimes) {
          let operations: ReturnType<typeof buildStoreOperationsSection> = null;
          if (wantsTimes) {
            // The operations patch has to echo the sections it isn't changing, so read the store
            // back rather than fabricating values that would clobber copied settings.
            const detail = await api.GET("/v1/stores/{store_name}", {
              params: { path: { store_name: body.storeName } },
            });
            if (detail.error || !detail.data) {
              patchError = toApiError(detail.error, detail.response, t("stores.bulkCreate.errors.hoursFailed")).message;
            } else {
              operations = buildStoreOperationsSection(row, sources, detail.data);
              if (!operations) patchError = t("stores.bulkCreate.errors.hoursUnavailable");
            }
          }
          // Skip the patch when hours were the only reason for it and the read-back failed.
          if (addressEnabled || operations) {
            const patch = await api.PATCH("/v1/stores/{store_name}", {
              params: { path: { store_name: body.storeName } },
              body: buildStoreEditBody(row, sources, createActive, { address: addressEnabled, operations }),
            });
            if (patch.error)
              patchError = toApiError(patch.error, patch.response, t("stores.bulkCreate.errors.patchFailed")).message;
          }
        }
        out.push({ rowIndex, status: "created", patchError });
      } catch (err) {
        // A 409 means the store name (or code within the chain) is already taken — nothing went
        // wrong, the row simply had nothing to do, so it's skipped rather than failed.
        const status: RowStatus = err instanceof ApiError && err.status === 409 ? "skipped" : "failed";
        out.push({ rowIndex, status, error: err instanceof Error ? err.message : String(err) });
      }
      setProgress(i + 1);
      setResults([...out]);
    }
    setRunning(false);
    queryClient.invalidateQueries({ queryKey: ["stores"] });
    queryClient.invalidateQueries({ queryKey: ["chains"] });
    queryClient.invalidateQueries({ queryKey: ["backoffice", "context"] });
  }

  function downloadFailures() {
    const failed = results.filter((r) => r.status === "failed" || r.patchError);
    const headers = [...STORE_IMPORT_FIELDS.map((f) => f.key), "error"];
    const rows = failed.map((r) => [
      ...STORE_IMPORT_FIELDS.map((f) => resolveField(sheet.rows[r.rowIndex] ?? [], sources, f.key)),
      r.error ?? r.patchError ?? "",
    ]);
    downloadCsv(`store-import-failures-${fileName || "sheet"}.csv`, headers, rows);
  }

  function renderSourceInput(field: StoreImportField) {
    const source = sources[field.key];
    if (source.mode === "computed") {
      const example = sheet.rows[0] ? resolveField(sheet.rows[0], sources, field.key) : "";
      // legibleName computed = a picked base column + " (store code)" appended.
      if (field.computedFromColumn) {
        return (
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <VirtualSelectPopover
                value={source.column != null ? String(source.column) : ""}
                onChange={(v) => patchSource(field.key, { column: v === "" ? null : Number(v) })}
                options={columnOptions}
                placeholder={t("bulkImport.selectColumn")}
                searchPlaceholder={t("bulkImport.searchColumns")}
                emptyText={t("bulkImport.noColumns")}
              />
            </div>
            <span className="shrink-0 whitespace-nowrap text-xs text-muted-foreground">
              {t("stores.bulkCreate.appendCode")}
            </span>
          </div>
        );
      }
      return (
        <span className="text-xs text-muted-foreground">
          {t("stores.bulkCreate.computedHint")}
          {example && <code className="ml-1.5 rounded bg-muted px-1.5 py-0.5 font-mono">{example}</code>}
        </span>
      );
    }
    if (source.mode === "constant") {
      if (field.constantInput === "chain") {
        return (
          <VirtualSelectPopover
            value={source.value}
            onChange={(v) => patchSource(field.key, { value: v })}
            options={chainOptions}
            placeholder={t("stores.form.selectChain")}
            searchPlaceholder={t("stores.form.searchChains")}
            emptyText={t("stores.form.noChains")}
            clearable
          />
        );
      }
      if (field.constantInput === "timezone") {
        return (
          <VirtualSelectPopover
            value={source.value}
            onChange={(v) => patchSource(field.key, { value: v })}
            options={TIMEZONE_OPTIONS}
            placeholder={t("stores.form.selectTimezone")}
            searchPlaceholder={t("stores.form.searchTimezones")}
            emptyText={t("stores.form.noTimezones")}
            clearable
          />
        );
      }
      if (field.constantInput === "time") {
        return (
          <Input
            type="time"
            value={source.value}
            onChange={(e) => patchSource(field.key, { value: e.target.value })}
            className="w-32"
          />
        );
      }
      if (field.constantInput === "country") {
        return (
          <VirtualSelectPopover
            value={source.value}
            onChange={(v) => patchSource(field.key, { value: v })}
            options={COUNTRY_OPTIONS}
            placeholder={t("stores.form.selectCountry")}
            searchPlaceholder={t("stores.form.searchCountries")}
            emptyText={t("stores.form.noCountries")}
            clearable
          />
        );
      }
      return (
        <Input
          value={source.value}
          onChange={(e) => patchSource(field.key, { value: e.target.value })}
          placeholder={t("bulkImport.valuePlaceholder")}
        />
      );
    }
    // column mode — use the same searchable popover as the chain/timezone pickers
    const unmapped = field.required && source.column == null;
    return (
      <VirtualSelectPopover
        value={source.column != null ? String(source.column) : ""}
        onChange={(v) => patchSource(field.key, { column: v === "" ? null : Number(v) })}
        options={columnOptions}
        placeholder={t("bulkImport.selectColumn")}
        searchPlaceholder={t("bulkImport.searchColumns")}
        emptyText={t("bulkImport.noColumns")}
        className={cn(unmapped && "border-destructive")}
      />
    );
  }

  function renderFieldRow(field: StoreImportField) {
    const source = sources[field.key];
    const modes: FieldSourceMode[] = field.computable ? ["column", "constant", "computed"] : ["column", "constant"];
    return (
      <div key={field.key} className="grid grid-cols-[7.5rem_auto_1fr] items-center gap-3">
        <Label className="text-sm">
          {t(STORE_FIELD_LABEL_KEY[field.key])}
          {field.required && <span className="ml-0.5 text-destructive">*</span>}
        </Label>
        <div className="inline-flex rounded-md border p-0.5">
          {modes.map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => patchSource(field.key, { mode })}
              className={cn(
                "rounded px-2 py-1 text-xs transition-colors",
                source.mode === mode
                  ? "bg-accent font-medium text-accent-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {t(`bulkImport.mode.${mode}`)}
            </button>
          ))}
        </div>
        <div className="min-w-0">{renderSourceInput(field)}</div>
      </div>
    );
  }

  const total = sheet.rows.length;
  const createdCount = results.filter((r) => r.status === "created").length;
  const skippedCount = results.filter((r) => r.status === "skipped").length;
  const failedCount = results.filter((r) => r.status === "failed").length;
  const patchFailedCount = results.filter((r) => r.status === "created" && r.patchError).length;
  const issues = results.filter((r) => r.status !== "created" || r.patchError);
  const pct = runTotal > 0 ? Math.round((progress / runTotal) * 100) : 0;
  // Hold the preview until the full store list has landed, so "already exists" is decided against
  // the whole estate (inactive included) rather than whatever had loaded at that moment.
  const canProceedToPreview = validation.requiredConfigured && validation.validCount > 0 && !allStoresPending;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn(
          "p-0 gap-0 overflow-hidden flex flex-col",
          // Fixed width (not w-fit) so the table doesn't resize the dialog; wide content scrolls
          // inside. Only the mapping and preview steps need the wide layout for their field/preview
          // table — upload and the progress report are compact.
          step === "map" || step === "preview" ? "sm:max-w-[64rem]" : "sm:max-w-2xl",
        )}
        aria-describedby={undefined}
      >
        {/* Cap the row at 85vh minus the footer; the body flexes and scrolls inside it (mirrors the
            Import Data dialog) so the footer stays pinned regardless of content height. */}
        <div className="flex flex-row max-h-[calc(85vh-4rem)]">
          <WizardPane icon={FileSpreadsheet} />
          <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 px-6 pt-6">
            <DialogHeader>
              <DialogTitle>{t("stores.bulkCreate.title")}</DialogTitle>
            </DialogHeader>

            <div className="-mx-6 min-h-0 flex-1 overflow-y-auto px-6 pb-6">
              {/* ── Step: upload ── */}
              {step === "upload" && (
                <div className="py-2">
                  <FileDropZone
                    accept=".xlsx,.xls,.csv"
                    onSelect={setFile}
                    selected={file ? { name: file.name, size: file.size } : undefined}
                    title={t("bulkImport.uploadPrompt")}
                    subtitle={t("bulkImport.uploadHint")}
                  />
                  {parseError && <p className="mt-3 text-sm text-destructive">{parseError}</p>}
                </div>
              )}

              {/* ── Step: map ── */}
              {step === "map" && (
                <div className="flex flex-col gap-4">
                  <div className="flex flex-wrap items-center gap-3 text-sm">
                    <span className="flex items-center gap-1.5 text-muted-foreground">
                      <FileSpreadsheet className="h-4 w-4" />
                      {fileName}
                    </span>
                    <span className="ml-auto text-muted-foreground">{t("bulkImport.rowCount", { count: total })}</span>
                  </div>

                  {sheetNames.length > 1 && (
                    <div className="grid grid-cols-[7.5rem_1fr] items-center gap-3">
                      <Label>{t("bulkImport.sheet")}</Label>
                      <VirtualSelectPopover
                        value={sheetName}
                        onChange={handleSheetChange}
                        options={sheetOptions}
                        placeholder={t("bulkImport.selectSheet")}
                        searchPlaceholder={t("bulkImport.searchSheets")}
                        emptyText={t("bulkImport.noSheetsFound")}
                      />
                    </div>
                  )}

                  <Separator />

                  {/* Field sources */}
                  <div className="flex flex-col gap-2.5">
                    {STORE_IMPORT_FIELDS.map(renderFieldRow)}
                    {/* Active flag — create stores directly in the chosen state (no create-then-deactivate). */}
                    <div className="flex items-center gap-3">
                      <Switch id="bulk-active" checked={createActive} onCheckedChange={setCreateActive} />
                      <Label htmlFor="bulk-active">{t("stores.bulkCreate.active")}</Label>
                    </div>
                  </div>

                  <Separator />

                  {/* Optional store & camera hours — patched onto each store after creation, since
                      the create endpoint defaults them server-side and takes no override. */}
                  <div className="flex flex-col gap-2.5">
                    <div className="flex items-center gap-3">
                      <Switch id="bulk-hours" checked={hoursEnabled} onCheckedChange={setHoursEnabled} />
                      <Label htmlFor="bulk-hours">{t("stores.bulkCreate.includeHours")}</Label>
                    </div>
                    {hoursEnabled && [...HOURS_IMPORT_FIELDS, ...CAMERA_IMPORT_FIELDS].map(renderFieldRow)}
                  </div>

                  <Separator />

                  {/* Optional address section — patched onto each store after creation. */}
                  <div className="flex flex-col gap-2.5">
                    <div className="flex items-center gap-3">
                      <Switch id="bulk-address" checked={addressEnabled} onCheckedChange={setAddressEnabled} />
                      <Label htmlFor="bulk-address">{t("stores.bulkCreate.includeAddress")}</Label>
                    </div>
                    {addressEnabled && ADDRESS_IMPORT_FIELDS.map(renderFieldRow)}
                  </div>

                  <Separator />

                  {/* Optional: copy settings from an existing store (create endpoint's copyFrom). */}
                  <div className="flex flex-col gap-2.5">
                    <div className="flex items-center gap-3">
                      <Switch id="bulk-copy" checked={copyEnabled} onCheckedChange={setCopyEnabled} />
                      <Label htmlFor="bulk-copy">{t("stores.form.copySettings")}</Label>
                    </div>
                    {copyEnabled && (
                      <div className="grid grid-cols-[7.5rem_1fr] items-center gap-3">
                        <Label>{t("stores.form.storeName")}</Label>
                        <VirtualSelectPopover
                          value={copyFrom}
                          onChange={setCopyFrom}
                          options={storeOptions}
                          placeholder={t("stores.form.selectCopyFrom")}
                          searchPlaceholder={t("stores.form.searchCopyFrom")}
                          emptyText={t("stores.form.noCopyFrom")}
                          clearable
                        />
                      </div>
                    )}
                  </div>

                  <Separator />

                  {/* Preview (read-only sample; same table as the select step) */}
                  <ImportPreviewTable
                    rows={previewRows}
                    fields={previewFields}
                    getCell={getCell}
                    maxRows={MAP_PREVIEW_ROWS}
                  />
                  {total > MAP_PREVIEW_ROWS && (
                    <p className="text-xs text-muted-foreground">
                      {t("bulkImport.previewNote", { shown: MAP_PREVIEW_ROWS, total })}
                    </p>
                  )}

                  <p className="text-sm">
                    {t("bulkImport.validSummary", {
                      valid: validation.validCount,
                      invalid: validation.invalidCount,
                    })}
                  </p>
                  {!validation.requiredConfigured && (
                    <p className="text-sm text-destructive">{t("bulkImport.errors.requiredUnmapped")}</p>
                  )}
                </div>
              )}

              {/* ── Step: preview (select which rows to create) ── */}
              {step === "preview" && (
                <div className="flex flex-col gap-3">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                    <span>{t("bulkImport.selectedSummary", { selected: selected.size, total })}</span>
                    {existsCount > 0 && (
                      <span className="flex items-center gap-1.5 text-amber-600 dark:text-amber-500">
                        <TriangleAlert className="h-3.5 w-3.5" />
                        {t("stores.bulkCreate.existsSummary", { count: existsCount })}
                      </span>
                    )}
                    {validation.invalidCount > 0 && (
                      <span className="flex items-center gap-1.5 text-destructive">
                        <CircleX className="h-3.5 w-3.5" />
                        {t("bulkImport.invalidSummary", { count: validation.invalidCount })}
                      </span>
                    )}
                  </div>

                  <ImportPreviewTable
                    rows={previewRows}
                    fields={previewFields}
                    getCell={getCell}
                    selectable
                    selected={selected}
                    onToggle={toggleRow}
                    onToggleAll={toggleAll}
                    allSelected={allSelectableSelected}
                  />
                </div>
              )}

              {/* ── Step: run ── */}
              {step === "run" && (
                <div className="flex flex-col gap-4 py-2">
                  <div className="flex items-center gap-3">
                    <div className="h-2 flex-1 overflow-hidden rounded bg-muted">
                      <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="tabular-nums text-sm text-muted-foreground">
                      {progress}/{runTotal}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    {running && <InlineSpinner label={t("stores.bulkCreate.creating")} className="py-0" />}
                    <span className="flex items-center gap-1.5 text-primary">
                      <CircleCheck className="h-4 w-4" />
                      {t("bulkImport.createdCount", { count: createdCount })}
                    </span>
                    {skippedCount > 0 && (
                      <span className="flex items-center gap-1.5 text-muted-foreground">
                        <SkipForward className="h-4 w-4" />
                        {t("bulkImport.skippedCount", { count: skippedCount })}
                      </span>
                    )}
                    <span className={cn("flex items-center gap-1.5", failedCount > 0 && "text-destructive")}>
                      <CircleX className="h-4 w-4" />
                      {t("bulkImport.failedCount", { count: failedCount })}
                    </span>
                    {patchFailedCount > 0 && (
                      <span className="flex items-center gap-1.5 text-amber-600 dark:text-amber-500">
                        <TriangleAlert className="h-4 w-4" />
                        {t("stores.bulkCreate.patchFailedCount", { count: patchFailedCount })}
                      </span>
                    )}
                  </div>

                  {issues.length > 0 && (
                    <div className="max-h-64 overflow-y-auto rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-16">{t("bulkImport.rowColumn")}</TableHead>
                            <TableHead>{t("stores.form.storeName")}</TableHead>
                            <TableHead>{t("bulkImport.errorColumn")}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {issues.map((r) => (
                            <TableRow key={r.rowIndex}>
                              <TableCell className="tabular-nums text-muted-foreground">{r.rowIndex + 1}</TableCell>
                              <TableCell className="whitespace-nowrap">
                                {resolveField(sheet.rows[r.rowIndex] ?? [], sources, "storeName") || "—"}
                              </TableCell>
                              {/* Skipped rows read as informational, not as something that broke. */}
                              <TableCell
                                className={cn(
                                  r.status === "failed" && "text-destructive",
                                  r.status === "created" && "text-amber-600 dark:text-amber-500",
                                  r.status === "skipped" && "text-muted-foreground",
                                )}
                              >
                                {r.status === "created" && t("stores.bulkCreate.patchIssue", { error: r.patchError })}
                                {r.status === "skipped" && t("stores.bulkCreate.skippedExists")}
                                {r.status === "failed" && r.error}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
          {step === "upload" && (
            <>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                {t("buttons.cancel")}
              </Button>
              <Button onClick={parseFile} disabled={!file}>
                {t("bulkImport.next")}
              </Button>
            </>
          )}
          {step === "map" && (
            <>
              <Button variant="outline" onClick={() => setStep("upload")}>
                {t("bulkImport.back")}
              </Button>
              <Button onClick={goToPreview} disabled={!canProceedToPreview}>
                {t("bulkImport.next")}
              </Button>
            </>
          )}
          {step === "preview" && (
            <>
              <Button variant="outline" onClick={() => setStep("map")}>
                {t("bulkImport.back")}
              </Button>
              <Button onClick={run} disabled={selected.size === 0}>
                {t("stores.bulkCreate.createButton", { count: selected.size })}
              </Button>
            </>
          )}
          {step === "run" && (
            <>
              {/* Skipped rows are listed as issues but aren't failures — don't offer an empty CSV. */}
              {!running && failedCount + patchFailedCount > 0 && (
                <Button variant="outline" onClick={downloadFailures}>
                  {t("bulkImport.downloadFailures")}
                </Button>
              )}
              <Button onClick={() => handleOpenChange(false)} disabled={running}>
                {running ? t("stores.bulkCreate.creating") : t("buttons.close")}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
